import { botThemes } from "@/pages/cbb-builder/AiBotData";
import { axiosGet } from "@/services/axiosService";
import getTextColor from "@/utils/getTextColor";

export const DEFAULT_RUBIK_FONT = {
  fontURL:
    "https://fonts.chatbotbuilder.net/1757314257282-s7i_9LEQCnlF-Rubik_Regular.css",
  fontFamily: "Rubik Regular",
  displayName: "Rubik Regular",
};

type BrandFont = typeof DEFAULT_RUBIK_FONT & {
  _id?: string;
};

const DEFAULT_CHATBOTBUILDER_LOGO =
  "https://dlvkyia8i4zmz.cloudfront.net/tROVfVoT56KLU61Yt0hY_svgviewer_output__3_.svg";

const DEFAULT_AGENT_AVATAR = {
  isPreMade: true,
  svgId: 1,
  url: "",
};

const brandFontCache = new Map<string, BrandFont>();

function getRenderableImageUrl(value: unknown) {
  if (typeof value !== "string") return "";

  try {
    const imageUrl = value.trim();
    const url = new URL(imageUrl);

    return url.protocol === "https:" ? imageUrl : "";
  } catch {
    return "";
  }
}

export function getBrandingContent(payload: any) {
  const content =
    payload?.fetchbranding?.data?.content ||
    payload?.fetchbranding?.content ||
    payload?.data?.content ||
    payload?.content ||
    null;

  if (content && typeof content === "object") {
    const reasoning =
      payload?.fetchbranding?.data?.brandingResult?.__llm_button_reasoning ||
      payload?.fetchbranding?.brandingResult?.__llm_button_reasoning ||
      payload?.data?.brandingResult?.__llm_button_reasoning ||
      payload?.brandingResult?.__llm_button_reasoning ||
      (content as any)?.brandingResult?.__llm_button_reasoning ||
      payload?.fetchbranding?.data?.__llm_button_reasoning ||
      payload?.fetchbranding?.__llm_button_reasoning ||
      payload?.data?.__llm_button_reasoning ||
      payload?.__llm_button_reasoning ||
      (content as any)?.__llm_button_reasoning;

    if (reasoning) {
      return {
        ...content,
        __llm_button_reasoning: reasoning,
      };
    }
  }

  return content;
}

function getFetchedFontFamily(brandingContent: any) {
  return (
    brandingContent?.typography?.fontFamilies?.primary ||
    brandingContent?.typography?.fontFamilies?.heading ||
    brandingContent?.fonts?.[0]?.family ||
    ""
  ).trim();
}

function parseColorToRgb(color: string) {
  const rgbMatch = color.match(
    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([0-9.]+))?\)/i,
  );

  if (rgbMatch) {
    return {
      r: Math.min(Math.max(parseInt(rgbMatch[1], 10), 0), 255),
      g: Math.min(Math.max(parseInt(rgbMatch[2], 10), 0), 255),
      b: Math.min(Math.max(parseInt(rgbMatch[3], 10), 0), 255),
    };
  }

  const hexMatch = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!hexMatch) return null;

  let hex = hexMatch[1];
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

function toRgbString(color: { r: number; g: number; b: number }) {
  return `rgb(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(color.b)})`;
}

function mixWithWhite(
  color: { r: number; g: number; b: number },
  amount = 0.35,
) {
  return {
    r: color.r + (255 - color.r) * amount,
    g: color.g + (255 - color.g) * amount,
    b: color.b + (255 - color.b) * amount,
  };
}

function getPrimaryColorStyles(primaryColor?: string, isDark = false) {
  const theme = isDark ? botThemes.dark : botThemes.light;
  const gradientBase = isDark ? "rgb(34, 34, 34)" : "rgb(255, 255, 255)";

  if (!primaryColor) {
    return {
      color: theme.botBubbleColor,
      welcomePageBgColor: theme.welcomePageBgColor,
      welcomePageBtnColor: theme.welcomePageBtnColor,
    };
  }

  const primaryRgb = parseColorToRgb(primaryColor);
  if (!primaryRgb) {
    return {
      color: primaryColor,
      welcomePageBgColor: `linear-gradient(0deg, ${gradientBase} 0%, ${primaryColor} 100%)`,
      welcomePageBtnColor: `linear-gradient(90deg, ${primaryColor} 0%, ${primaryColor} 100%)`,
    };
  }

  const primaryRgbString = toRgbString(primaryRgb);
  const lighterPrimaryString = toRgbString(mixWithWhite(primaryRgb));

  return {
    color: primaryRgbString,
    welcomePageBgColor: `linear-gradient(0deg, ${gradientBase} 0%, ${primaryRgbString} 100%)`,
    welcomePageBtnColor: `linear-gradient(240deg, ${lighterPrimaryString} 0%, ${primaryRgbString} 100%)`,
  };
}

async function getAvailableBrandFont(brandingContent: any) {
  const fontFamily = getFetchedFontFamily(brandingContent);
  if (!fontFamily) return DEFAULT_RUBIK_FONT;

  const cacheKey = fontFamily.toLowerCase();
  const cachedFont = brandFontCache.get(cacheKey);
  if (cachedFont) return cachedFont;

  try {
    const response = await axiosGet("/users/getFonts", {
      params: {
        page: 1,
        limit: 10,
        search: fontFamily,
      },
    });

    const fonts = response?.data?.data ?? [];
    const matchedFont = fonts.find((font: any) => {
      const name = `${font?.name ?? ""}`.trim().toLowerCase();
      return font?.isActive && name === fontFamily.toLowerCase();
    });

    if (!matchedFont) {
      brandFontCache.set(cacheKey, DEFAULT_RUBIK_FONT);
      return DEFAULT_RUBIK_FONT;
    }

    const brandFont = {
      fontURL: matchedFont.url || DEFAULT_RUBIK_FONT.fontURL,
      fontFamily: matchedFont.name,
      displayName: matchedFont.name,
      _id: matchedFont._id,
    };

    brandFontCache.set(cacheKey, brandFont);
    return brandFont;
  } catch (error) {
    brandFontCache.set(cacheKey, DEFAULT_RUBIK_FONT);
    return DEFAULT_RUBIK_FONT;
  }
}

export async function buildChatConfigFromBranding(
  currentChatConfig: any,
  brandingContent: any,
) {
  const llmButtonPrimary =
    brandingContent?.__llm_button_reasoning?.primary ||
    brandingContent?.brandingResult?.__llm_button_reasoning?.primary;
  let llmPrimaryColor: string | undefined;
  if (llmButtonPrimary) {
    if (llmButtonPrimary.color) {
      llmPrimaryColor = llmButtonPrimary.color;
    } else if (typeof llmButtonPrimary.reasoning === "string") {
      const match = llmButtonPrimary.reasoning.match(
        /#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/,
      );
      if (match) {
        llmPrimaryColor = match[0];
      }
    }
  }

  let primaryColor = brandingContent?.colors?.primary;
  const secondaryColor = brandingContent?.colors?.secondary;
  const logoUrl = getRenderableImageUrl(brandingContent?.images?.logo);
  const faviconUrl = getRenderableImageUrl(brandingContent?.images?.favicon);
  const fetchedFontFamily = getFetchedFontFamily(brandingContent);

  if (primaryColor) {
    const isWhite = (() => {
      const c = primaryColor.trim().toLowerCase();
      if (c === "#ffffff" || c === "#fff" || c === "white") return true;
      const rgb = parseColorToRgb(primaryColor);
      if (rgb && rgb.r === 255 && rgb.g === 255 && rgb.b === 255) return true;
      return false;
    })();

    if (isWhite && secondaryColor) {
      primaryColor = secondaryColor;
    }
  }

  if (!primaryColor && !logoUrl && !faviconUrl && !fetchedFontFamily) {
    return currentChatConfig;
  }

  const colorScheme =
    brandingContent?.colorScheme === "dark" ? "dark" : "light";
  const isDark = colorScheme === "dark";
  const activeTheme = isDark ? botThemes.dark : botThemes.light;

  const brandFont = await getAvailableBrandFont(brandingContent);
  const currentGreetingConfig = currentChatConfig?.greetingConfig || {};
  const currentTypography = currentChatConfig?.typography || {};
  const currentGreetingTypography = currentGreetingConfig?.typography || {};
  const primaryColorStyles = getPrimaryColorStyles(primaryColor, isDark);
  const resolvedLogo = logoUrl || faviconUrl || DEFAULT_CHATBOTBUILDER_LOGO;
  const accentColor = llmPrimaryColor;

  return {
    ...currentChatConfig,
    ...activeTheme,
    logo: resolvedLogo,
    theme: colorScheme,
    botBubbleColor: activeTheme.userBubbleColor,
    userBubbleColor: accentColor ? accentColor : primaryColorStyles.color,
    welcomePageBgColor: primaryColorStyles.welcomePageBgColor,
    welcomePageBtnColor: accentColor
      ? accentColor
      : primaryColorStyles.welcomePageBtnColor,
    welcomePageBtnTxtColor: getTextColor(
      accentColor ? accentColor : primaryColorStyles.color,
    ),
    typography: {
      ...activeTheme.typography,
      ...currentTypography,
      font: brandFont,
      color: getTextColor(activeTheme.userBubbleColor),
    },
    greetingConfig: {
      ...activeTheme.greetingConfig,
      ...currentGreetingConfig,
      header: {
        ...currentGreetingConfig?.header,
        color: getTextColor(primaryColorStyles.color),
      },
      description: {
        ...currentGreetingConfig?.description,
        color: getTextColor(primaryColorStyles.color),
      },
      typography: {
        ...activeTheme.greetingConfig.typography,
        ...currentGreetingTypography,
        font: brandFont,
        color: activeTheme.greetingConfig.typography.color,
      },
      headerFooterBgColor: primaryColorStyles.color,
    },
    botAvatar: resolvedLogo
      ? {
          ...(currentChatConfig?.botAvatar || {}),
          isPreMade: false,
          svgId: 0,
          url: resolvedLogo,
          fallbackUrl: logoUrl || DEFAULT_CHATBOTBUILDER_LOGO,
        }
      : DEFAULT_AGENT_AVATAR,
    sendBtnBgColor: accentColor ? accentColor : primaryColor,
  };
}
